import React from 'react';
import styled from 'styled-components';
import { theme } from '@theme';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  icon?: React.ReactNode;
  fullWidth?: boolean;
}

const InputWrapper = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
  width: ${props => (props.$fullWidth ? '100%' : 'auto')};
`;

const Label = styled.label`
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.text};
`;

const InputContainer = styled.div<{ $hasError?: boolean; $hasIcon?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;

  ${props =>
    props.$hasIcon &&
    `
    svg {
      position: absolute;
      left: ${theme.spacing.md};
      color: ${theme.colors.text}60;
      pointer-events: none;
    }
  `}
`;

const StyledInput = styled.input<{ $hasError?: boolean; $hasIcon?: boolean }>`
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  padding-left: ${props => (props.$hasIcon ? '40px' : theme.spacing.md)};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.base};
  color: ${theme.colors.text};
  background-color: ${theme.colors.white};
  border: 1px solid ${props => (props.$hasError ? theme.colors.error : theme.colors.surface)};
  border-radius: ${theme.borderRadius.md};
  transition: all ${theme.transitions.normal};

  &:hover:not(:disabled) {
    border-color: ${props => (props.$hasError ? theme.colors.error : theme.colors.accent)};
  }

  &:focus {
    outline: none;
    border-color: ${props => (props.$hasError ? theme.colors.error : theme.colors.accent)};
    box-shadow: 0 0 0 3px
      ${props => (props.$hasError ? theme.colors.error + '20' : theme.colors.accent + '20')};
  }

  &:disabled {
    background-color: ${theme.colors.surface};
    cursor: not-allowed;
    opacity: 0.6;
  }

  &::placeholder {
    color: ${theme.colors.text}40;
  }
`;

const HelperText = styled.span<{ $isError?: boolean }>`
  font-size: ${theme.typography.sizes.xs};
  color: ${props => (props.$isError ? theme.colors.error : theme.colors.text + '80')};
`;

export const Input: React.FC<InputProps> = ({
  label,
  error,
  helperText,
  icon,
  fullWidth = false,
  id,
  ...props
}) => {
  const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');
  const hasIcon = !!icon;
  const hasError = !!error;

  return (
    <InputWrapper $fullWidth={fullWidth}>
      {label && <Label htmlFor={inputId}>{label}</Label>}
      <InputContainer $hasError={hasError} $hasIcon={hasIcon}>
        {icon}
        <StyledInput id={inputId} $hasError={hasError} $hasIcon={hasIcon} {...props} />
      </InputContainer>
      {(error || helperText) && (
        <HelperText $isError={!!error}>{error || helperText}</HelperText>
      )}
    </InputWrapper>
  );
};
